import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';
import { useTimeSettings } from '../context/TimeSettingsContext';

import { useLinkSettings } from '../context/LinkSettingsContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useAlert } from '../context/ToastContext';
import { formatDuration, calculateAnimeTime, formatEpisodeProgress, getTotalEpisodes, getMediaStatusBadge } from '../utils/timeFormatter';
import { formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { updateAnimeStatus, updateAnimeProgress, updateMediaScore } from '../services/aniListService';
import { getSimpleCache } from '../services/cacheService';
import { LOCAL_STORAGE_KEYS } from '../config/constants';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import ConfirmationModal from './ConfirmationModal';
import GroupStatusModal from './GroupStatusModal';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';
import { CollectionsSkeleton } from './common/SkeletonLoader';
import './Collections.css';

const SORT_OPTIONS = [
    { id: 'name', label: '📝 Name', sortFn: (a, b, dir) => dir * a.name.localeCompare(b.name) },
    { id: 'averageScore', label: '⭐ Average Score', sortFn: (a, b, dir) => dir * (b.stats.averageScore - a.stats.averageScore) },
    { id: 'totalAnime', label: '📚 Total Anime', sortFn: (a, b, dir) => dir * (b.stats.animeCount - a.stats.animeCount) },
    { id: 'totalDuration', label: '⏱️ Total Duration', sortFn: (a, b, dir) => dir * (b.stats.totalDuration - a.stats.totalDuration) },
    { id: 'watchedTime', label: '✅ Watched Time', sortFn: (a, b, dir) => dir * (b.stats.watchedDuration - a.stats.watchedDuration) },
    { id: 'remainingTime', label: '⏳ Remaining Time', sortFn: (a, b, dir) => dir * ((b.stats.totalDuration - b.stats.watchedDuration) - (a.stats.totalDuration - a.stats.watchedDuration)) }
];

const COLLECTIONS_SORT_BY_KEY = 'collections_sort_by';
const COLLECTIONS_SORT_DIR_KEY = 'collections_sort_direction';

const Collections = () => {
    const location = useLocation();
    const { animeGroups, isLoading, isProcessingGroups, fetchProgress, clearGroupsCache, refreshData } = useAniList();
    const { titlePreference } = useTitlePreference();
    const { hoursPerDay } = useTimeSettings();

    const { watchLinks } = useLinkSettings();
    const { addChange, undo, redo, canUndo, canRedo } = useUndoRedo();
    const { showError, showWarning } = useAlert();
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [matchedAnime, setMatchedAnime] = useState(new Set());
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem(COLLECTIONS_SORT_BY_KEY) || 'name';
    });
    const [sortDirection, setSortDirection] = useState(() => {
        return localStorage.getItem(COLLECTIONS_SORT_DIR_KEY) || 'asc';
    });
    const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [selectedAnimeForModal, setSelectedAnimeForModal] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));

    const GROUPS_PER_PAGE = 15;

    // Debounce search query for performance (300ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Save sort options to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(COLLECTIONS_SORT_BY_KEY, sortBy);
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem(COLLECTIONS_SORT_DIR_KEY, sortDirection);
    }, [sortDirection]);

    // Filter and sort groups
    const filteredGroups = useMemo(() => {
        // Early return if no groups
        if (!animeGroups || animeGroups.length === 0) return [];

        let groups = animeGroups; // Reference, not copy (optimization)
        const matched = new Set(); // Track anime IDs that match search

        // Search filter - only create new array if search is active
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            const queryNoSpaces = query.replace(/\s+/g, '');

            groups = groups.filter(group => {
                // Check if group name matches
                const groupNameMatches = group.name.toLowerCase().includes(query) || group.name.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);

                // Check if any anime in the group matches (search both English and Romanji)
                const matchingAnime = group.anime.filter(entry => {
                    const englishTitle = entry.media.title.english || '';
                    const romanjiTitle = entry.media.title.romaji || '';

                    return englishTitle.toLowerCase().includes(query) ||
                        romanjiTitle.toLowerCase().includes(query) ||
                        englishTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces) ||
                        romanjiTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                });

                // Add matching anime IDs to the matched set
                matchingAnime.forEach(entry => matched.add(entry.media.id));

                // Include group if group name matches OR any anime matches
                return groupNameMatches || matchingAnime.length > 0;
            });
        }

        // Update matched anime state
        setMatchedAnime(matched);

        // Apply sorting
        const sortOption = SORT_OPTIONS.find(opt => opt.id === sortBy);
        if (sortOption) {
            const direction = sortDirection === 'asc' ? 1 : -1;
            groups = [...groups].sort((a, b) => sortOption.sortFn(a, b, direction));
        }

        return groups;
    }, [animeGroups, debouncedSearchQuery, sortBy, sortDirection]); // Use debouncedSearchQuery instead of searchQuery

    // Calculate paginated groups to display
    const displayedGroups = useMemo(() => {
        const totalPages = Math.ceil(filteredGroups.length / GROUPS_PER_PAGE);
        let groupsToShow = [];

        // Add groups from all loaded pages
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * GROUPS_PER_PAGE;
            const endIndex = startIndex + GROUPS_PER_PAGE;
            groupsToShow.push(...filteredGroups.slice(startIndex, endIndex));
        });

        return groupsToShow;
    }, [filteredGroups, loadedPages]);

    // Calculate total pages
    const totalPages = Math.ceil(filteredGroups.length / GROUPS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [debouncedSearchQuery, sortBy, sortDirection]);

    // BUFFERED PAGINATION: Immediately load Page 2 if available
    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Load next page
    const loadNextPage = useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 1000; // Load aggressively (1000px before bottom) to maintain buffer

        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    // Attach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const toggleGroup = useCallback((groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    }, [expandedGroups]);

    // Handle anime status change with optimistic updates
    const handleStatusChange = useCallback(async (mediaId, currentStatus, newStatus, animeTitle) => {
        if (newStatus === currentStatus) return;

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update - update UI immediately
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        // Add to undo history
        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle,
            oldStatus: currentStatus,
            newStatus: newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`✅ Updated ${animeTitle} to ${newStatus}`);
            // Trigger FORCE refresh to get fresh data from AniList (not cache)
            if (refreshData) {
                refreshData(true);
            }
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to update status for ${animeTitle}:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });

            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    }, [refreshData]);

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, animeTitle) => {
        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, tkn);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${animeTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle episode progress change
    const handleProgressChange = async (mediaId, newProgress, totalEpisodes, animeTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalEpisodes > 0) {
            clamped = Math.min(clamped, totalEpisodes);
        } else if (totalEpisodes === 0) {
            clamped = 0;
        }

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeProgress(mediaId, clamped, token);
            console.log(`✅ Updated ${animeTitle} progress to ${clamped}`);
            if (refreshData) {
                refreshData(true);
            }

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Watching)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${animeTitle} — progress set to ${clamped}, switching to Watching`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', animeTitle);
            }

            // Auto-complete: if all episodes watched and media is finished, mark as COMPLETED
            if (clamped > 0 && totalEpisodes > 0 && clamped === totalEpisodes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${animeTitle} — all ${totalEpisodes} episodes watched`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', animeTitle);
            }
        } catch (error) {
            console.error(`❌ Failed to update progress for ${animeTitle}:`, error);
            showError(`Failed to update progress for ${animeTitle}`, 'Update Failed');
            setOptimisticProgress(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
        } finally {
            setUpdatingProgress(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    // Handle undo
    const handleUndo = useCallback(async () => {
        const change = undo();
        if (!change) return;

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);

        // Check if this is a bulk operation
        if (change.type === 'BULK_STATUS_CHANGE') {
            const { changes, groupName } = change;
            console.log(`↩️ Undoing bulk change for ${groupName}`);

            setBulkProgress({ total: changes.length, completed: 0, failed: 0 });

            // Process each like single anime with optimistic updates
            for (const c of changes) {
                // Optimistic update
                setOptimisticUpdates(prev => new Map(prev).set(c.mediaId, c.oldStatus));
                setChangingStatus(prev => new Set([...prev, c.mediaId]));

                try {
                    await updateAnimeStatus(c.mediaId, c.oldStatus, token);
                    setChangingStatus(prev => {
                        const next = new Set(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                } catch (error) {
                    // Revert optimistic update on error
                    setOptimisticUpdates(prev => {
                        const next = new Map(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                    setChangingStatus(prev => {
                        const next = new Set(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                }

                setBulkProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
            }

            setTimeout(() => setBulkProgress(null), 2000);
            return;
        }

        // Single status change undo
        const { mediaId, oldStatus, animeTitle } = change;

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, oldStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeStatus(mediaId, oldStatus, token);
            console.log(`↩️ Undid change for ${animeTitle}`);

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to undo for ${animeTitle}:`, error);
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
            showError(`Failed to undo: ${error.message}`, 'Undo Failed');
        }
    }, [undo]);

    // Handle redo
    const handleRedo = useCallback(async () => {
        const change = redo();
        if (!change) return;

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);

        // Check if this is a bulk operation
        if (change.type === 'BULK_STATUS_CHANGE') {
            const { changes, groupName } = change;
            console.log(`↪️ Redoing bulk change for ${groupName}`);

            setBulkProgress({ total: changes.length, completed: 0, failed: 0 });

            // Process each like single anime with optimistic updates
            for (const c of changes) {
                // Optimistic update
                setOptimisticUpdates(prev => new Map(prev).set(c.mediaId, c.newStatus));
                setChangingStatus(prev => new Set([...prev, c.mediaId]));

                try {
                    await updateAnimeStatus(c.mediaId, c.newStatus, token);
                    setChangingStatus(prev => {
                        const next = new Set(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                } catch (error) {
                    // Revert optimistic update on error
                    setOptimisticUpdates(prev => {
                        const next = new Map(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                    setChangingStatus(prev => {
                        const next = new Set(prev);
                        next.delete(c.mediaId);
                        return next;
                    });
                }

                setBulkProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
            }

            setTimeout(() => setBulkProgress(null), 2000);
            return;
        }

        // Single status change redo
        const { mediaId, newStatus, animeTitle } = change;

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`↪️ Redid change for ${animeTitle}`);

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to redo for ${animeTitle}:`, error);
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
            showError(`Failed to redo: ${error.message}`, 'Redo Failed');
        }
    }, [redo]);
    // Handle bulk status change
    const handleBulkStatusChange = useCallback(async (groupId, mode, fromStatus, toStatus) => {
        const group = animeGroups.find(g => g.id === groupId);
        if (!group) return;

        // Filter anime based on mode
        let animeToChange;
        if (mode === 'all') {
            animeToChange = group.anime;
        } else {
            animeToChange = group.anime.filter(a => a.status === fromStatus);
        }

        if (animeToChange.length === 0) {
            showWarning('No anime match the criteria', 'No Matches');
            setBulkModalOpen(false);
            return;
        }

        const token = localStorage.getItem('anilist_token');
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Close modal and show progress
        setBulkModalOpen(false);

        // Optimistic UI updates - update immediately (batch all updates)
        setOptimisticUpdates(prev => {
            const next = new Map(prev);
            animeToChange.forEach(anime => {
                next.set(anime.media.id, toStatus);
            });
            return next;
        });

        // Trigger immediate FORCE refresh to update UI with fresh data
        if (refreshData) {
            refreshData(true);
        }

        setBulkProgress({ total: animeToChange.length, completed: 0, failed: 0 });

        // Add to undo history as bulk operation
        const changes = animeToChange.map(anime => ({
            mediaId: anime.media.id,
            animeTitle: getDisplayTitle(anime.media, titlePreference),
            oldStatus: anime.status,
            newStatus: toStatus
        }));

        addChange({
            type: 'BULK_STATUS_CHANGE',
            groupId: groupId,
            groupName: group.name,
            changes: changes,
            timestamp: Date.now()
        });

        // Process in batches of 3
        const errors = [];
        for (let i = 0; i < animeToChange.length; i += 3) {
            const batch = animeToChange.slice(i, i + 3);

            const results = await Promise.allSettled(
                batch.map(anime => updateAnimeStatus(anime.media.id, toStatus, token))
            );

            results.forEach((result, idx) => {
                if (result.status === 'rejected') {
                    errors.push(batch[idx].media.id);
                }
            });

            setBulkProgress(prev => ({
                ...prev,
                completed: Math.min(i + batch.length, animeToChange.length),
                failed: errors.length
            }));

            // Delay between batches
            if (i + 3 < animeToChange.length) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        // Refresh data with force to get fresh status
        if (refreshData) {
            refreshData(true);
        }

        // Clear progress after 2 seconds
        setTimeout(() => setBulkProgress(null), 2000);

        if (errors.length > 0) {
            console.warn(`⚠️ Failed to update ${errors.length}/${animeToChange.length} anime`);
        } else {
            console.log(`✅ Successfully updated ${animeToChange.length} anime`);
        }
    }, [animeGroups, refreshData]);
    const openBulkModal = useCallback((groupId) => {
        const group = animeGroups.find(g => g.id === groupId);
        setSelectedGroup(group);
        setBulkModalOpen(true);
    }, [animeGroups]);


    // Handle URL parameter for auto-expanding a specific group (instant scroll for performance)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const expandGroupId = searchParams.get('expandGroup');

        if (expandGroupId && animeGroups.length > 0) {
            // Set expanded groups
            setExpandedGroups(new Set([expandGroupId]));

            // Instant scroll without animation for better performance with many groups
            setTimeout(() => {
                const element = document.getElementById(`group-${expandGroupId}`);
                if (element) {
                    // behavior: 'auto' = instant jump, block: 'center' = center in viewport
                    element.scrollIntoView({ behavior: 'auto', block: 'center' });

                    // Update saved scroll position for this route
                    setTimeout(() => {
                        const positions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
                        positions['/groups'] = window.scrollY;
                        sessionStorage.setItem('scrollPositions', JSON.stringify(positions));
                    }, 200);
                }
            }, 100);
        }
    }, [location.search, animeGroups]);

    const getStatusBadge = (status) => {
        const badges = {
            'CURRENT': { emoji: '▶️', label: 'Watching', color: '#4CAF50' },
            'COMPLETED': { emoji: '✅', label: 'Completed', color: '#2196F3' },
            'PLANNING': { emoji: '📅', label: 'Planning', color: '#FF9800' },
            'PAUSED': { emoji: '⏸️', label: 'On Hold', color: '#9E9E9E' },
            'DROPPED': { emoji: '❌', label: 'Dropped', color: '#F44336' },
            'REPEATING': { emoji: '🔄', label: 'Rewatching', color: '#9C27B0' }
        };
        const badge = badges[status] || { emoji: '❓', label: status, color: '#757575' };
        return (
            <span className="status-badge" style={{ backgroundColor: badge.color }}>
                {badge.emoji} {badge.label}
            </span>
        );
    };

    // Show skeleton during initial load
    if (isLoading && (!animeGroups || animeGroups.length === 0)) {
        return <CollectionsSkeleton />;
    }

    if (isProcessingGroups) {
        return (
            <div className="groups-container">
                <div className="loading-state">
                    <div className="loader"></div>
                    <h3>🔄 {fetchProgress.status}</h3>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${fetchProgress.progress}%` }}
                        ></div>
                    </div>
                    <p>{Math.round(fetchProgress.progress)}% complete</p>
                    <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '8px' }}>
                        💡 If progress appears stuck, don't worry - it's still working in the background
                    </p>
                </div>
            </div>
        );
    }

    if (!animeGroups || animeGroups.length === 0) {
        return (
            <div className="groups-container">
                <div className="empty-state">
                    <div className="empty-icon">📚</div>
                    <h3>No Groups Yet</h3>
                    <p>Groups will be built automatically from your anime list.</p>
                    <p>Make sure you're connected to AniList.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="groups-container">
            <div className="groups-header">
                <h1>📚 anime groups</h1>
                <p>Your anime organized by relation groups</p>
                <div className="header-buttons">
                    <button
                        onClick={() => setShowRebuildConfirm(true)}
                        className="force-rebuild-btn"
                        title="Clear cache and rebuild groups"
                    >
                        🔄 Force Rebuild Groups
                    </button>

                    <div className="undo-redo-buttons">
                        <button
                            onClick={handleUndo}
                            disabled={!canUndo}
                            className="undo-btn"
                            title="Undo last status change"
                        >
                            ↩️ Undo
                        </button>
                        <button
                            onClick={handleRedo}
                            disabled={!canRedo}
                            className="redo-btn"
                            title="Redo last undone change"
                        >
                            ↪️ Redo
                        </button>
                    </div>
                </div>

                <ConfirmationModal
                    isOpen={showRebuildConfirm}
                    onConfirm={() => {
                        clearGroupsCache();
                        setShowRebuildConfirm(false);
                    }}
                    onCancel={() => setShowRebuildConfirm(false)}
                    title="Rebuild Collections?"
                    message="This will clear the cached data and fetch fresh information from AniList to rebuild all your anime groups."
                    confirmText="Rebuild Now"
                    cancelText="Cancel"
                />
            </div>

            {/* Search and Sort Bar */}
            <div className="search-sort-container">
                <input
                    type="text"
                    className="search-input"
                    placeholder="🔍 Search groups or anime..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="sort-controls">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                        title="Sort by"
                    >
                        {SORT_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                        className="sort-direction-btn"
                        title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>



            {/* Groups List */}
            <div className="groups-list">
                {filteredGroups.length === 0 ? (
                    <div className="empty-state">
                        <p>No groups found for this filter{searchQuery && ` matching "${searchQuery}"`}.</p>
                        {searchQuery && (
                            <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>Check the spelling or try different keywords</p>
                        )}
                    </div>
                ) : (
                    <>
                        {displayedGroups.map(group => {
                            const isExpanded = expandedGroups.has(group.id);
                            // Get the oldest anime (first in sorted array) for the cover image
                            const oldestAnime = group.anime[0];
                            const coverImage = oldestAnime?.media?.coverImage?.extraLarge || oldestAnime?.media?.coverImage?.large || oldestAnime?.media?.coverImage?.medium;
                            // Compute group name dynamically so it updates when titlePreference changes
                            const groupName = getDisplayTitle(oldestAnime?.media, titlePreference);
                            const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                            return (
                                <div key={group.id} id={`group-${group.id}`} className={`group-card ${isExpanded ? 'expanded' : ''}`}>
                                    {/* Group Header */}
                                    <div
                                        className="group-header"
                                        onClick={() => toggleGroup(group.id)}
                                    >
                                        {coverImage && (
                                            <img
                                                src={coverImage}
                                                alt={groupName}
                                                className="group-cover"
                                            />
                                        )}
                                        <div className="group-info">
                                            <h3 className="group-name">
                                                {isExpanded ? '📖' : '📚'} {groupName}
                                            </h3>
                                            {hasReleasing && <span className="group-releasing-tag">📡 Airing</span>}
                                            <div className="group-stats">
                                                <span className="stat-anime-count">{group.stats.animeCount} anime</span>
                                                <span>•</span>
                                                <span className="stat-episodes">
                                                    {formatEpisodeProgress(group.stats.watchedEpisodes, group.stats.totalEpisodes)} eps
                                                </span>
                                                {group.stats.averageScore > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-score">⭐ {(group.stats.averageScore / 10).toFixed(1)}</span>
                                                    </>
                                                )}
                                                <span>•</span>
                                                <span className="stat-total-time">📺 {formatDuration(group.stats.totalDuration, hoursPerDay)}</span>
                                                {group.stats.watchedDuration > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-watched-time">✅ {formatDuration(group.stats.watchedDuration, hoursPerDay)}</span>
                                                    </>
                                                )}
                                                {(group.stats.totalDuration - group.stats.watchedDuration) > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-remaining-time">⏳ {formatDuration(group.stats.totalDuration - group.stats.watchedDuration, hoursPerDay)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            className="bulk-status-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openBulkModal(group.id);
                                            }}
                                            title="Change status for all anime in group"
                                        >
                                            ⚙️ Bulk
                                        </button>
                                        <button className="expand-btn">
                                            {isExpanded ? '▼' : '▶'}
                                        </button>
                                    </div>

                                    {/* Group Content (Expanded) */}
                                    {isExpanded && (
                                        <div className="group-content">
                                            <div className="anime-grid">
                                                {group.anime.map((entry, index) => {
                                                    const title = getDisplayTitle(entry.media, titlePreference);
                                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                                    const totalEpisodes = getTotalEpisodes(entry);
                                                    const watchedEpisodes = entry.progress || 0;
                                                    const animeTime = calculateAnimeTime(entry);
                                                    const isMatched = matchedAnime.has(entry.media.id);
                                                    // Get optimistic status if available, otherwise use actual status
                                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;
                                                    const watchedEpisodesDisplay = optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : watchedEpisodes;

                                                    return (
                                                        <div key={entry.id} className="anime-item">
                                                            <div className="anime-cover-wrapper">
                                                                <div className="anime-index">{index + 1}</div>
                                                                <img
                                                                    src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                                    alt={title}
                                                                    className="anime-cover"
                                                                />
                                                            </div>
                                                            <div className="anime-details">
                                                                <h4 className="anime-title">
                                                                    <a
                                                                        href={`https://anilist.co/anime/${entry.media.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {title}
                                                                    </a>
                                                                </h4>
                                                                <div className="anime-action-buttons">
                                                                    <ScoreInput
                                                                        mediaId={entry.media.id}
                                                                        currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                                        isUpdating={updatingScores.has(entry.media.id)}
                                                                        onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                                    />
                                                                    <EpisodeCounter
                                                                        mediaId={entry.media.id}
                                                                        currentProgress={watchedEpisodesDisplay}
                                                                        totalEpisodes={totalEpisodes}
                                                                        isUpdating={updatingProgress.has(entry.media.id)}
                                                                        onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalEpisodes, title, entry.media.status, displayStatus)}
                                                                    />
                                                                    <StatusButton
                                                                        currentStatus={displayStatus}
                                                                        onChange={(newStatus) => handleStatusChange(entry.media.id, displayStatus, newStatus, title)}
                                                                        disabled={changingStatus.has(entry.media.id)}
                                                                        isLoading={changingStatus.has(entry.media.id)}
                                                                    />
                                                                    <button
                                                                        className="action-btn watch-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedAnimeForModal(title);
                                                                            setShowWatchModal(true);
                                                                        }}
                                                                        title="Watch"
                                                                    >
                                                                        🎬
                                                                    </button>
                                                                </div>
                                                                <div className="anime-meta">
                                                                    <span>{releaseDate}</span>
                                                                    <span>{entry.media.format}</span>
                                                                    <span>{formatEpisodeProgress(watchedEpisodes, totalEpisodes)} eps</span>
                                                                    {entry.media.averageScore && <span>⭐ {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                                    <span>📺 {formatDuration(animeTime.total, hoursPerDay)}</span>
                                                                    {animeTime.watched > 0 && (
                                                                        <span>✅ {formatDuration(animeTime.watched, hoursPerDay)}</span>
                                                                    )}
                                                                    {animeTime.remaining > 0 && (
                                                                        <span>⏳ {formatDuration(animeTime.remaining, hoursPerDay)}</span>
                                                                    )}
                                                                    {(() => { const badge = getMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {/* Load More Button */}
                        {loadedPages.size < totalPages && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <button
                                    onClick={loadNextPage}
                                    className="force-rebuild-btn"
                                    style={{ margin: '0 auto' }}
                                >
                                    📥 Load More ({displayedGroups.length} / {filteredGroups.length})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Summary Footer */}
            <div className="groups-summary">
                <p>
                    Showing {displayedGroups.length} of {filteredGroups.length} groups (Total: {animeGroups.length})
                    {searchQuery && ` for "${searchQuery}"`}
                    {loadedPages.size < totalPages && ` • Page ${loadedPages.size} of ${totalPages}`}
                </p>

            </div>

            {/* Bulk Status Modal */}
            <GroupStatusModal
                isOpen={bulkModalOpen}
                group={selectedGroup}
                optimisticUpdates={optimisticUpdates}
                onConfirm={handleBulkStatusChange}
                onCancel={() => setBulkModalOpen(false)}
            />

            {/* Bulk Progress Indicator */}
            {
                bulkProgress && (
                    <div className="bulk-progress-overlay">
                        <div className="bulk-progress-card">
                            <h3>⚙️ Updating Status...</h3>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <p>{bulkProgress.completed} / {bulkProgress.total} anime updated</p>
                            {bulkProgress.failed > 0 && (
                                <p className="error-text">⚠️ {bulkProgress.failed} failed</p>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Watch Modal */}
            <SiteLinksModal
                isOpen={showWatchModal}
                onClose={() => {
                    setShowWatchModal(false);
                    setSelectedAnimeForModal(null);
                }}
                links={watchLinks}
                animeName={selectedAnimeForModal || ''}
                type="watch"
            />


        </div >
    );
};

export default React.memo(Collections);
